import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

const TYPE_PRESETS = [
  { value: 'PRIVACY_POLICY', label: '개인정보처리방침 (PRIVACY_POLICY)' },
  { value: 'PRIVACY_COLLECT', label: '개인정보 수집 및 이용 (PRIVACY_COLLECT)' },
  { value: 'TERMS_OF_SERVICE', label: '이용약관 (TERMS_OF_SERVICE)' },
  { value: 'CUSTOM', label: '직접 입력' },
];

const emptyForm = {
  type: 'PRIVACY_POLICY',
  customType: '',
  title: '',
  content: '',
};

const DUPLICATE_TYPE_MESSAGE = '이미 등록된 타입입니다. 기존 항목을 수정하거나 다른 타입을 사용해주세요.';

const formatSaveError = (error) => {
  if (error?.code === '23505') return DUPLICATE_TYPE_MESSAGE;
  return error?.message || '저장 중 오류가 발생했습니다.';
};

const CommonMessageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new' || !id;

  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setLoading(false);
      return;
    }
    loadData();
  }, [id, isNew]);

  const loadData = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const { data, error } = await supabase
        .from('common_message')
        .select('id, type, title, content')
        .eq('id', id)
        .single();
      if (error) throw error;

      const isPreset = TYPE_PRESETS.some((item) => item.value === data.type);
      setForm({
        type: isPreset ? data.type : 'CUSTOM',
        customType: isPreset ? '' : data.type,
        title: data.title || '',
        content: data.content || '',
      });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '데이터 조회 중 오류가 발생했습니다.' });
    } finally {
      setLoading(false);
    }
  };

  const resolvedType = (form.type === 'CUSTOM' ? form.customType : form.type).trim();

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (!resolvedType) {
      setMessage({ type: 'error', text: '타입을 입력해주세요.' });
      return false;
    }
    if (!form.title.trim()) {
      setMessage({ type: 'error', text: '제목을 입력해주세요.' });
      return false;
    }
    if (!form.content.trim()) {
      setMessage({ type: 'error', text: '내용을 입력해주세요.' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setMessage(null);
    const payload = {
      type: resolvedType,
      title: form.title.trim(),
      content: form.content,
    };
    try {
      if (isNew) {
        const { data, error } = await supabase.from('common_message').insert([payload]).select('id').single();
        if (error) throw error;
        navigate(`/common-messages/${data.id}`, { replace: true });
        setMessage({ type: 'success', text: '저장되었습니다.' });
      } else {
        const { error } = await supabase.from('common_message').update(payload).eq('id', id);
        if (error) throw error;
        setMessage({ type: 'success', text: '수정되었습니다.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: formatSaveError(error) });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    if (!window.confirm('이 공통 메시지를 삭제할까요?')) return;
    setSaving(true);
    setMessage(null);
    try {
      const { error } = await supabase.from('common_message').delete().eq('id', id);
      if (error) throw error;
      navigate('/common-messages');
    } catch (error) {
      setMessage({ type: 'error', text: error.message || '삭제 중 오류가 발생했습니다.' });
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="card shadow mb-4"><div className="card-body">조회 중...</div></div>;
  }

  return (
    <div className="card shadow mb-4">
      <div className="card-header py-3 d-flex justify-content-between align-items-center">
        <h6 className="m-0 font-weight-bold text-primary">
          {isNew ? '공통 메시지 등록' : '공통 메시지 수정'}
        </h6>
        <div>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm mr-2"
            onClick={() => navigate('/common-messages')}
            disabled={saving}
          >
            목록
          </button>
          {!isNew && (
            <button
              type="button"
              className="btn btn-outline-danger btn-sm mr-2"
              onClick={handleDelete}
              disabled={saving}
            >
              삭제
            </button>
          )}
          <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>

      <div className="card-body">
        {message && (
          <div className={`alert ${message.type === 'error' ? 'alert-danger' : 'alert-success'}`} role="alert">
            {message.text}
          </div>
        )}

        <div className="form-group">
          <label>타입</label>
          <select
            className="form-control"
            name="type"
            value={form.type}
            onChange={handleChange}
            disabled={!isNew}
          >
            {TYPE_PRESETS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          {form.type === 'CUSTOM' && (
            <input
              type="text"
              className="form-control mt-2"
              name="customType"
              value={form.customType}
              onChange={handleChange}
              placeholder="예: NOTICE_BANNER"
              disabled={!isNew}
            />
          )}
          <small className="form-text text-muted">타입은 고유값이며 앱/웹에서 이 값으로 조회합니다. 수정 시에는 변경할 수 없습니다.</small>
        </div>

        <div className="form-group">
          <label>제목</label>
          <input
            type="text"
            className="form-control"
            name="title"
            value={form.title}
            onChange={handleChange}
            placeholder="개인정보처리방침"
          />
        </div>

        <div className="form-group">
          <label>내용 (HTML)</label>
          <textarea
            className="form-control"
            name="content"
            rows="18"
            value={form.content}
            onChange={handleChange}
            placeholder="<h2>제목</h2><p>본문</p>"
            style={{ fontFamily: 'Menlo, Monaco, Consolas, monospace', fontSize: 13 }}
          />
        </div>

        {form.content && (
          <div className="form-group mb-0">
            <label>미리보기</label>
            <div
              className="border rounded p-3 bg-white"
              style={{ maxHeight: 360, overflowY: 'auto' }}
              dangerouslySetInnerHTML={{ __html: form.content }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonMessageDetail;
